import {
  computeAscentDescentFromElevations,
  computeAscentDescentMeters,
  computeAverageCadence,
  computeAverageHeartRate,
  computeAveragePaceSecPerKm,
  computeAverageSpeedMetersPerSecond,
  computeMaxHeartRate,
  computeMaxSpeedMetersPerSecond,
  computeMovingTimeSec,
  computeTiming,
  computeTotalDistanceMeters,
  recalculated,
  unavailable,
} from '@fit-editor/fit-core'
import type { Activity, ActivityEvent, ActivityRecord, SummaryValue } from '@fit-editor/fit-core'

import { createUnitFormatters, formatDurationSeconds } from '@/lib/format'
import type { UnitSystem } from '@/lib/format'
import type { TimeRangeMs } from '@/store/activity-store'

/**
 * Stats for a time range selected on the timeline. Pure: slices the
 * activity to the range and runs fit-core's existing (provenance-tagged)
 * summary calculations over the slice -- no calculation logic of its own.
 * Display-only: nothing here is written back to the activity.
 */

export interface ActivitySlice {
  /** The activity restricted to the range (records + events); laps/sessions kept as-is. */
  activity: Activity
  /** Index into the *full* activity's `records` of each sliced record, in order -- for slicing index-aligned overlays (terrain). */
  recordIndices: number[]
}

const TIMER_STOP_TYPES = new Set(['stop', 'stopAll'])

/** Whether the timer is stopped just before `startMs`, per the same timer-event semantics as fit-core's `computeTiming`. */
function timerStoppedBefore(events: ActivityEvent[], startMs: number): boolean {
  const timerEvents = events
    .filter(
      (e) => e.event === 'timer' && e.timestamp instanceof Date && e.timestamp.getTime() < startMs,
    )
    .sort((a, b) => (a.timestamp as Date).getTime() - (b.timestamp as Date).getTime())
  let stopped = false
  for (const e of timerEvents) {
    const type = typeof e.eventType === 'string' ? e.eventType : undefined
    if (!stopped && type != null && TIMER_STOP_TYPES.has(type)) stopped = true
    else if (stopped && type === 'start') stopped = false
  }
  return stopped
}

/**
 * Restricts `activity` to `start <= timestamp <= end` (inclusive both
 * ends): records and events with a timestamp in range. Laps/sessions are
 * kept unchanged (the per-record calcs don't use them; cadence's sport
 * lookup reads sessions).
 *
 * If the timer was stopped when the range begins (the stop event lies
 * before the range, the matching start inside it), a slice of only
 * in-range events would lose that stop and count the paused stretch as
 * timer/moving time. So a synthetic timer `stop` is prepended at the
 * slice's first timestamp -- an input to the calculations only, never
 * written to the file.
 */
export function sliceActivityToRange(activity: Activity, range: TimeRangeMs): ActivitySlice {
  const { start, end } = range
  const inRange = (d: unknown): d is Date =>
    d instanceof Date && d.getTime() >= start && d.getTime() <= end

  const records: ActivityRecord[] = []
  const recordIndices: number[] = []
  activity.records.forEach((record, i) => {
    if (inRange(record.timestamp)) {
      records.push(record)
      recordIndices.push(i)
    }
  })
  const events = activity.events.filter((e) => inRange(e.timestamp))

  if (timerStoppedBefore(activity.events, start)) {
    const times = [...records, ...events]
      .map((m) => (m.timestamp as Date).getTime())
      .filter((t) => Number.isFinite(t))
    const first = times.length > 0 ? Math.min(...times) : start
    events.unshift({ event: 'timer', eventType: 'stop', timestamp: new Date(first) })
  }

  return { activity: { ...activity, records, events }, recordIndices }
}

export interface SelectionStat {
  key: string
  label: string
  value: SummaryValue<unknown>
  /** Formatted value, or "Not available" for an `unavailable` value. */
  display: string
}

function formatHeartRateBpm(bpm: number): string {
  return `${Math.round(bpm)} bpm`
}

function formatCadenceValue(cadence: { value: number; unit: string }): string {
  return `${Math.round(cadence.value)} ${cadence.unit}`
}

function stat<T>(
  key: string,
  label: string,
  value: SummaryValue<T>,
  format: (v: T) => string,
): SelectionStat {
  return {
    key,
    label,
    value,
    display: value.value == null ? 'Not available' : format(value.value),
  }
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/**
 * Which metric families the *whole* activity has any data for -- a stat
 * whose family is absent entirely (e.g. no heart-rate sensor) is hidden
 * rather than listed as "Not available"; a family that exists but has no
 * value inside the selection still shows "Not available".
 */
function availableFamilies(records: ActivityRecord[]) {
  return {
    distance: records.some(
      (r) => isNum(r.distance) || (isNum(r.positionLat) && isNum(r.positionLong)),
    ),
    // `computeMaxSpeedMetersPerSecond` reads the legacy `speed` field.
    maxSpeed: records.some((r) => isNum(r.speed)),
    moving: records.some((r) => isNum(r.speed) || isNum(r.enhancedSpeed) || isNum(r.distance)),
    altitude: records.some((r) => isNum(r.altitude) || isNum(r.enhancedAltitude)),
    heartRate: records.some((r) => isNum(r.heartRate)),
    cadence: records.some((r) => isNum(r.cadence)),
  }
}

/**
 * Stats for `range` of `activity`. `terrainValues`, when given, must be
 * index-aligned to `activity.records` (the terrain overlay); terrain
 * ascent/descent are included only then. `units` only affects the
 * formatted `display` strings, never `value`.
 */
export function computeSelectionStats(
  activity: Activity,
  range: TimeRangeMs,
  terrainValues?: (number | null)[],
  units: UnitSystem = 'metric',
): SelectionStat[] {
  const fmt = createUnitFormatters(units)
  const slice = sliceActivityToRange(activity, range)
  const { records } = slice.activity
  const has = availableFamilies(activity.records)
  const empty = !records.some((r) => r.timestamp instanceof Date)

  const timing = computeTiming(slice.activity)
  const elapsed: SummaryValue<number> = empty ? unavailable() : recalculated(timing.elapsedTimeSec)
  const timer: SummaryValue<number> = empty ? unavailable() : recalculated(timing.timerTimeSec)
  const distance = computeTotalDistanceMeters(records)
  const { ascent, descent } = computeAscentDescentMeters(records)

  const stats: SelectionStat[] = [
    stat('elapsed', 'Elapsed', elapsed, formatDurationSeconds),
    stat('timer', 'Timer', timer, formatDurationSeconds),
  ]
  if (has.moving) {
    stats.push(
      stat('moving', 'Moving', computeMovingTimeSec(slice.activity), formatDurationSeconds),
    )
  }
  if (has.distance) {
    stats.push(
      stat('distance', 'Distance', distance, fmt.distance),
      stat(
        'avgSpeed',
        'Avg speed',
        computeAverageSpeedMetersPerSecond(distance, timing.timerTimeSec),
        fmt.speed,
      ),
    )
  }
  if (has.maxSpeed) {
    stats.push(stat('maxSpeed', 'Max speed', computeMaxSpeedMetersPerSecond(records), fmt.speed))
  }
  if (has.distance) {
    stats.push(
      stat(
        'avgPace',
        'Avg pace',
        computeAveragePaceSecPerKm(distance, timing.timerTimeSec),
        fmt.pace,
      ),
    )
  }
  if (has.altitude) {
    stats.push(
      stat('ascent', 'Ascent', ascent, fmt.elevation),
      stat('descent', 'Descent', descent, fmt.elevation),
    )
  }
  if (terrainValues != null && terrainValues.length === activity.records.length) {
    const terrain = computeAscentDescentFromElevations(
      slice.recordIndices.map((i) => terrainValues[i] ?? null),
    )
    stats.push(
      stat('terrainAscent', 'Ascent (terrain)', terrain.ascent, fmt.elevation),
      stat('terrainDescent', 'Descent (terrain)', terrain.descent, fmt.elevation),
    )
  }
  if (has.heartRate) {
    stats.push(
      stat('avgHeartRate', 'Avg HR', computeAverageHeartRate(records), formatHeartRateBpm),
      stat('maxHeartRate', 'Max HR', computeMaxHeartRate(records), formatHeartRateBpm),
    )
  }
  if (has.cadence) {
    stats.push(
      stat('avgCadence', 'Avg cadence', computeAverageCadence(slice.activity), formatCadenceValue),
    )
  }
  return stats
}
